import React, { useId } from 'react';

/**
 * Inline SVG sparkline — cheap enough for one per table row (recharts is not).
 * Flat-zero series render as a faint baseline rather than nothing, so an idle
 * tenant is visibly idle instead of visibly broken.
 */
export const Sparkline: React.FC<{
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
  className?: string;
}> = ({ data, width = 96, height = 26, stroke = 'var(--color-primary-dark)', className }) => {
  const gid = useId();
  if (!data || data.length < 2) return <span className="text-xs text-text-muted">—</span>;

  const max = Math.max(...data, 1);
  const stepX = width / (data.length - 1);
  const pad = 2;
  const pts = data.map((v, i) => {
    const x = i * stepX;
    const y = height - pad - (v / max) * (height - pad * 2);
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const total = data.reduce((a, b) => a + b, 0);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={`${total} in the last ${data.length} days`}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={total === 0 ? 0 : 0.25} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={total === 0 ? 0.3 : 1}
        className="spark-draw"
      />
    </svg>
  );
};
